import React from 'react';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { body1ToParagraphOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useTranslation } from '@shared/core';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { pxToRem } from '@withjoy/joykit/theme';
import { Flex, TextV2 } from '@withjoy/joykit';
import { Category } from '@graphql/generated';
import { distanceInMiles, getTravelDistance } from '@shared/utils/distanceInMiles';
import { WalkIcon } from '@assets/icons';
import { Car } from '@withjoy/joykit/icons';

export const VenueDistance = ({ milesToVenue }: { milesToVenue: string }) => {
  const [fontPackExperimentEnabled] = useIsFeatureEnabled('fontPackExperiment');
  const { t } = useTranslation('guestSiteCustom');
  const accTrans = t('accommodations');
  const { scaleFontSize } = useScaleGuestSiteFontSize();
  return (
    <GuestSiteTypographyOverride override={body1ToParagraphOverride} defaultFontFamily="Inter UI">
      <TextV2
        typographyVariant="body1"
        color="mono14"
        {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
        fontSize={scaleFontSize(pxToRem(15), Category.PARAGRAPH)}
        fontWeight={400}
        lineHeight={1.5}
      >
        {accTrans.milesFromVenue({ miles: milesToVenue })}
      </TextV2>
    </GuestSiteTypographyOverride>
  );
};

export const TravelDistance = ({ milesToVenue }: { milesToVenue: string }) => {
  const { t } = useTranslation('guestSiteCustom');
  const accTrans = t('accommodations');
  const travelDistance = getTravelDistance(parseFloat(milesToVenue));
  const travelDistanceLabel = travelDistance.isWalkableDistance ? accTrans.minWalk({ minWalk: travelDistance.minutes }) : accTrans.minDrive({ minDrive: travelDistance.minutes });
  return (
    <Flex width="fit-content" alignItems="center">
      <GuestSiteTypographyOverride override={body1ToParagraphOverride} defaultFontFamily="Inter UI">
        {travelDistance?.isWalkableDistance ? <WalkIcon color="mono14" /> : <Car size={16} color="mono14" />}
        <TextV2 typographyVariant="body1" color="mono14" lineHeight={1.5} marginLeft={1} fontSize={15} maxWidth={{ _: pxToRem(180), sm: pxToRem(260) }} truncate>
          {travelDistanceLabel}
        </TextV2>
      </GuestSiteTypographyOverride>
    </Flex>
  );
};

export const VenueAndTravelDistance = ({
  locationCoordinates,
  hotelCoordinates
}: {
  locationCoordinates: { latitude?: number | null; longitude?: number | null };
  hotelCoordinates: { latitude?: number | null; longitude?: number | null };
}) => {
  const haveDistanceToVenue = locationCoordinates.latitude && locationCoordinates.longitude && hotelCoordinates.latitude && hotelCoordinates.longitude;
  const milesToVenue = distanceInMiles(
    locationCoordinates.latitude || 0,
    locationCoordinates.longitude || 0,
    hotelCoordinates.latitude || 0,
    hotelCoordinates.longitude || 0
  ).toFixed(1);

  if (!haveDistanceToVenue) {
    return null;
  }

  return (
    <Flex gap={4} alignItems="center" marginTop={1}>
      <VenueDistance milesToVenue={milesToVenue} />
      <TravelDistance milesToVenue={milesToVenue} />
    </Flex>
  );
};
